import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiService from '../services/ApiService';
import { isRunningInBiltNativeShell, requestNativeLogout } from '../utils/nativeShell';

interface User {
  id: number;
  username: string;
  email: string;
  role?: string;
  can_organize?: boolean;
  can_judge?: boolean;
  first_name?: string;
  last_name?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  isOrganizer: boolean;
  isJudge: boolean;
  sessionExpiringSoon: boolean;
  timeUntilExpiry: number | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => void;
  extendSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);

  const checkAuthStatus = () => {
    setIsLoading(true);

    try {
      const isAuth = apiService.isAuthenticated();
      const currentUser = apiService.getCurrentUser();

      if (isAuth && currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await apiService.login(username, password);
      setUser(response.user);
      setSessionExpiringSoon(false);
      setTimeUntilExpiry(null);
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);

    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setSessionExpiringSoon(false);
      setTimeUntilExpiry(null);
      if (isRunningInBiltNativeShell()) {
        requestNativeLogout();
      }
      setIsLoading(false);
    }
  };

  const extendSession = async (): Promise<boolean> => {
    try {
      const refreshedTokens = await apiService.refreshToken();
      if (refreshedTokens) {
        setSessionExpiringSoon(false);
        setTimeUntilExpiry(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session extension failed:', error);
      return false;
    }
  };

  // Enhanced auth event listeners
  useEffect(() => {
    const handleAuthExpired = (_event: CustomEvent) => {
      setUser(null);
      setSessionExpiringSoon(false);
      setTimeUntilExpiry(null);
      // Toast via AuthSessionToasts (ToastContext) — avoid DOM-injected duplicates
    };

    const handleAuthExpiringSoon = (event: CustomEvent) => {
      setSessionExpiringSoon(true);
      setTimeUntilExpiry(event.detail.timeUntilExpiry);
    };

    const handleAuthChanged = () => {
      try {
        const isAuth = apiService.isAuthenticated();
        const currentUser = apiService.getCurrentUser();
        if (isAuth && currentUser) {
          setUser(currentUser);
          setSessionExpiringSoon(false);
          setTimeUntilExpiry(null);
        } else if (!isAuth) {
          setUser(null);
        }
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    };

    window.addEventListener('auth-expired', handleAuthExpired as EventListener);
    window.addEventListener('auth-expiring-soon', handleAuthExpiringSoon as EventListener);
    window.addEventListener('bilt-auth-changed', handleAuthChanged);

    // Check initial auth status
    checkAuthStatus();

    // Set up periodic auth checks (every 30 seconds)
    const authCheckInterval = setInterval(() => {
      if (user) {
        apiService.isAuthenticated(); // This will trigger events if needed
      }
    }, 30000);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired as EventListener);
      window.removeEventListener('auth-expiring-soon', handleAuthExpiringSoon as EventListener);
      window.removeEventListener('bilt-auth-changed', handleAuthChanged);
      clearInterval(authCheckInterval);
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isSuperAdmin: user?.role === 'super_admin',
    isOrganizer: Boolean(user?.can_organize) || user?.role === 'super_admin' || user?.role === 'organizer',
    isJudge: Boolean(user?.can_judge) || user?.role === 'super_admin' || user?.role === 'judge',
    sessionExpiringSoon,
    timeUntilExpiry,
    login,
    logout,
    checkAuthStatus,
    extendSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
